/* Application-detail layout, ported 1:1 from the Claude Design prototype
 * (pim-admin/pages.jsx ApplicationPage). DS Card supplies the white card shell;
 * these styles reproduce the bespoke header, card-heads, prow rows, access
 * tags and the delete card that the DS does not provide. */

/* two-column layout — design `.grid-360` (1.55fr / 1fr) */
.grid {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: var(--spacing-4);
  align-items: start;
}
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}
@media (width <= 1000px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

/* ── header card ── */
.headerRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  flex-wrap: wrap;
  padding: var(--spacing-5);
}
.headerLeft {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  min-width: 0;
}
.markSquare {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: var(--color-grey-lightest);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.title {
  font-size: var(--font-size-xl);
  letter-spacing: -0.01em;
  color: var(--color-text);
}
.metaRow {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  margin-top: var(--spacing-1);
  flex-wrap: wrap;
}
.idPill {
  border: none;
  cursor: pointer;
  background: var(--color-grey-lightest);
  padding: 2px 7px;
  border-radius: 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--font-size-s);
  color: var(--color-text);
}
.idPill:hover {
  background: var(--color-grey-lighter);
}

/* inline code pill — design `.codepill` (non-interactive, e.g. contract IDs) */
.codepill {
  display: inline-block;
  background: var(--color-grey-lightest);
  padding: 2px 7px;
  border-radius: 5px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: var(--font-size-s);
  color: var(--color-text);
}
.metaLink {
  font-size: var(--font-size-s);
  color: var(--color-primary);
}
.headerActions {
  display: flex;
  gap: var(--spacing-2);
  flex-shrink: 0;
  align-items: center;
}

/* ── card head (bordered, with leading icon) ── */
.cardHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-5);
  border-bottom: 1px solid var(--color-grey-lightest);
  min-height: 60px;
}
.cardHeadTitle {
  font-size: var(--font-size-m);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text);
}
.cardHeadActions {
  display: flex;
  gap: var(--spacing-2);
  align-items: center;
}
.count {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  font-variant-numeric: tabular-nums;
}

/* lighter header used by the access-tag cards (design `.h3` row) */
.tagHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-3);
}
.tagHeadTitle {
  font-size: var(--font-size-m);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text);
}

.bodyPad {
  padding: var(--spacing-5);
}
.bodyFlush {
  padding: 0;
}

/* ── linked-client prow row ── */
.prow {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px var(--spacing-4);
  cursor: pointer;
  text-align: left;
  width: 100%;
  border: none;
  background: transparent;
  font-family: inherit;
}
.prow:hover {
  background: var(--color-primary-lightest);
}
.omark {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--color-primary-lighter);
  color: var(--color-primary-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.prowMain {
  flex: 1;
  min-width: 0;
}
.prowName {
  font-size: var(--font-size-m);
  color: var(--color-text);
}
.prowMeta {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 2px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.prowChev {
  color: var(--color-grey-light);
  flex-shrink: 0;
}
.clientEmpty {
  padding: 14px var(--spacing-4);
  font-size: var(--font-size-m);
}

/* ── delete card (design `.gated`) ── */
.gated {
  border: 1px solid var(--color-error-lighter);
  border-radius: 12px;
  padding: var(--spacing-4) 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--color-grey-lightest);
}
.gatedIc {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-white);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-error);
}
.gatedMain {
  flex: 1;
  min-width: 0;
}
.gatedTitle {
  font-size: var(--font-size-m);
  color: var(--color-text);
}
.gatedText {
  font-size: var(--font-size-s);
  color: var(--color-grey);
  margin-top: 2px;
}

/* ── access picktags ── */
.tagWrap {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2);
}
.picktag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 7px 5px 11px;
  border: 1px solid var(--color-grey-lighter);
  border-radius: 999px;
  font-size: var(--font-size-s);
  background: var(--color-white);
  max-width: 100%;
}
.picktagLbl {
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.picktagLbl:hover {
  text-decoration: underline;
}
.picktagX {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: var(--color-grey-lightest);
  color: var(--color-grey);
  cursor: pointer;
  flex-shrink: 0;
}
.picktagX:hover {
  background: var(--color-error-lightest);
  color: var(--color-error);
}
.empty {
  font-size: var(--font-size-m);
  color: var(--color-grey);
}
